/**
 * D11 -- a sealed row, read by a person.
 *
 * The chain entries printed the canonical fields exactly as the seal
 * holds them: `payload_hash=9f2c...` and sixty-four more characters,
 * `derivation_version=2`, `captured_by=worker:blast_radius`, all in one
 * run. Those are the bytes the hash is computed over, which is why they
 * are stored that way and why they must not change -- but the surface
 * built for a supervisor was showing him the input to a hash function
 * and calling it a record.
 *
 * Two separate jobs, kept apart:
 *
 *   * `sealedFieldLabel` names the field. Explicit, for the same reason
 *     the workflow catalogue is: no transform knows that `biz_crit` is
 *     read "business criticality" or that `tokens_in` is about a
 *     language model rather than money.
 *
 *   * `sealedFieldValue` renders it. A 64-character hash is an identity
 *     nobody reads in full and everybody compares the ends of, so it is
 *     shortened at both ends rather than truncated at one -- a prefix
 *     alone cannot be checked against anything.
 *
 * NOTHING here changes what was sealed. The canonical bytes are what
 * the verifier re-walks; this is presentation over the top of them, and
 * the raw value stays available as the element's title.
 */
const FIELD_LABELS: Record<string, string> = {
  // Every chain.
  entity_kind: "Subject type",
  entity_id: "Subject",
  captured_at: "Sealed at",
  captured_by: "Sealed by",
  // The derivers.
  payload_hash: "Payload fingerprint",
  derivation_version: "Deriver version",
  inputs_hash: "Inputs fingerprint",
  total_nodes: "Nodes reached",
  process_count: "Customer journeys",
  obligation_count: "Obligations",
  reasons_canonical: "Reasons",
  state: "State",
  // Governance ceremonies.
  decision_id: "Decision",
  decision_version: "Decision version",
  pack_id: "Evidence pack",
  pack_version: "Pack version",
  scores_hash: "Scores fingerprint",
  composite: "Composite score",
  tier: "Tier",
  recommendation: "Recommendation",
  inclusion_decision: "Inclusion decision",
  criticality_tier: "Criticality tier",
  pathway: "Assessment pathway",
  methodology_version: "Methodology version",
  score_str: "Score",
  confidence_str: "Confidence",
  posture_str: "Posture",
  exposure_str: "Exposure",
  outcome_str: "Outcome",
  index_str: "Maturity index",
  // Service methodology.
  classification: "Classification",
  biz_crit: "Business criticality",
  cyber_crit: "Cyber criticality",
  rto_hours: "Recovery time objective (hours)",
  rpo_hours: "Recovery point objective (hours)",
  financial_impact_sar: "Financial impact per hour (SAR)",
  // Engine output + analysers.
  surface: "Engine surface",
  /* Round 12 C1 -- this is the model that ANSWERED now, read off the
     provider's own response. It used to be MARSAD_AI_MODEL from the
     deployment's environment, sealed under this name. */
  model: "Model that answered",
  mode: "Mode",
  run_id: "Run",
  run_kind: "Run type",
  prompt_hash: "Prompt fingerprint",
  prompt_sha256: "Prompt fingerprint",
  prompt_version: "Prompt version",
  response_hash: "Response fingerprint",
  response_sha256: "Response fingerprint",
  grounded: "Citations that resolved",
  dropped_ungrounded: "Citations dropped as invented",
  tokens_in: "Tokens sent",
  tokens_out: "Tokens returned",
  suggested_status: "Suggested status",
  suggested_ml: "Suggested maturity level",
  suggested_summary: "Suggested summary",
  suggested_gaps: "Suggested gaps",
  suggested_recommendations: "Suggested recommendations",
  suggested_fields_canonical: "Suggested fields",
  assessment_id: "Assessment",
  assessment_version: "Assessment version",
  control_id: "Control",
  cycle_id: "Assessment cycle",
  service_id: "Service",
  // Connectors + settings + documents.
  enabled: "Enabled",
  cadence_seconds: "Cadence (seconds)",
  source: "Recorded by",
  key: "Setting",
  value: "Value",
  data_type: "Type",
  updated_by_user_id: "Changed by",
  actor_user_id: "Acted by",
  /* Round 16 H18, ninth round -- the framework-assessment chain's
     grader. Round 15 fixed the record card's helper and left this one:
     "Graded by user id: Dana Preparer". */
  graded_by_user_id: "Graded by",
  batch_id: "Ingest batch",
  filename: "File",
  sha256: "File fingerprint",
  size_bytes: "Size (bytes)",
  // Examiner grants.
  examiner_username: "Examiner",
  scope_note: "Scope",
  expires_at_str: "Expires",
  action: "Action",
  /* Round 12 C2 -- "Rows returned" is what the chain calls the number
     of items the Engine offered on that call, and on a narration
     surface it is 1 by construction: one question, one note. An
     examiner reading "Rows returned: 1" on every row of every Engine
     surface learns nothing, and the word "rows" invites him to think it
     counts something about the bank. It counts claims the Engine made
     and the grounding check then judged. */
  total_returned: "Claims the Engine offered",
  /* C1 -- what the deployment ASKED for, beside `model`, which is what
     answered. The first was the second under the second's name until
     round 12. */
  configured_model: "Model configured",
};

export function sealedFieldLabel(name: string): string {
  const known = FIELD_LABELS[name];
  if (known) return known;
  // An uncatalogued field degrades to something readable rather than
  // disappearing -- and keeps its shape, so a gap here is visible
  // rather than silently plausible.
  // A `_user_id` field's value is resolved to a person before it is
  // printed, so its label names what they did, not the id (H18).
  const base = name.endsWith("_user_id") ? name.slice(0, -"_user_id".length) : name;
  const spaced = base.replace(/_/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** A hex fingerprint: long, and compared at both ends. */
const HEX = /^[0-9a-f]{32,}$/i;
const ISO = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}/;

export interface RenderedValue {
  /** What to show. */
  text: string;
  /** The full value, for the title attribute -- never hidden, only
   *  folded. */
  full: string;
  /** True when the display form is shorter than the stored one. */
  shortened: boolean;
}

/**
 * ROUND 13 H -- an absent sealed value says so.
 *
 * "Graded by" printed nothing after its colon, at seven rounds;
 * "Derived from run" printed nothing on every seeded entry. A blank
 * after a label is the one rendering this product refuses everywhere
 * else: it reads as a value somebody failed to write, when for a
 * seeded row there was never a run to record.
 *
 * Deliberately not per-field. Saying WHY each particular field is
 * empty would mean guessing at the reason from the field name, and a
 * confident wrong reason on a sealed record is worse than an honest
 * "nothing was recorded here".
 */
export const NOT_RECORDED = "not recorded";

export function sealedFieldValue(value: unknown): RenderedValue {
  if (value === null || value === undefined || String(value).trim() === "") {
    return { text: NOT_RECORDED, full: NOT_RECORDED, shortened: false };
  }
  const full = String(value);
  if (HEX.test(full)) {
    return {
      text: `${full.slice(0, 8)}…${full.slice(-8)}`,
      full,
      shortened: true,
    };
  }
  if (ISO.test(full)) {
    // Seconds, no microseconds and no trailing Z: the precision below a
    // second is real and is not what anybody reads a seal for.
    return { text: full.slice(0, 19).replace("T", " "), full, shortened: true };
  }
  return { text: full, full, shortened: false };
}
